// SceneLights — global lighting for the Personal Planet Observatory.
// The directional light's *position, color, and intensity* all lerp
// toward the active scene's anchor (read from window.__planetProgress)
// so the planet experiences a real day/night cycle as the user
// scrolls: warm midday (Wake) -> gold-hour (About) -> low light
// (Skills) -> cool blue moonlight (Projects) -> pre-dawn (Notes) ->
// morning (Timeline) -> final gold hour (Contact).
//
// Hemisphere intensity also rides a day/night curve from 0.6 (midday)
// to 0.3 (night). Ambient stays constant for stability. The tracking
// mint point light still follows the active landmark.

import { useFrame } from '@react-three/fiber';
import { useMemo, useRef } from 'react';
import * as THREE from 'three';
import type { DirectionalLight, PointLight } from 'three';

import { SCENES, type SceneConfig } from '../planet/planetSections';
import { sceneAtProgress } from '../planet/sceneState';

const HEMI_TOP = '#efe8dc';
const HEMI_BOTTOM = '#17201f';
const AMBIENT_COLOR = '#dce8ff';
const TRACK_COLOR = '#9fd2b9';
const RIM_COLOR = '#c6ad78';

// Shared scratch state — keep allocations off the hot path.
interface LightScratch {
  pos: THREE.Vector3;
  color: THREE.Color;
}
function makeScratch(): LightScratch {
  return {
    pos: new THREE.Vector3(5, 5, 5),
    color: new THREE.Color('#f5f0e8'),
  };
}

export interface SceneLightsProps {
  /** Optional override of the active scene. */
  activeSceneIndex?: number;
}

export function SceneLights({ activeSceneIndex }: SceneLightsProps) {
  const dirRef = useRef<DirectionalLight | null>(null);
  const hemiRef = useRef<THREE.HemisphereLight | null>(null);
  const trackRef = useRef<PointLight | null>(null);
  const rimRef = useRef<PointLight | null>(null);
  const cameraFillRef = useRef<PointLight | null>(null);
  const tmpPos = useMemo(() => new THREE.Vector3(), []);
  const rimPos = useMemo(() => new THREE.Vector3(), []);
  const fillPos = useMemo(() => new THREE.Vector3(), []);
  const accentColor = useMemo(() => new THREE.Color(), []);
  const scratch = useMemo(() => makeScratch(), []);

  useFrame(({ camera, clock }) => {
    const p =
      typeof window !== 'undefined' && typeof window.__planetProgress === 'number'
        ? window.__planetProgress
        : 0;
    const { idx } = sceneAtProgress(p);
    const scene: SceneConfig = SCENES[idx] ?? SCENES[0];
    if (!scene) return;

    // ---- Directional light: position + color + intensity lerp. ----
    if (dirRef.current) {
      scratch.pos.set(...scene.lightPos);
      dirRef.current.position.lerp(scratch.pos, 0.06);

      scratch.color.set(scene.lightColor);
      dirRef.current.color.lerp(scratch.color, 0.06);

      const curI = dirRef.current.intensity;
      dirRef.current.intensity = curI + (scene.lightIntensity - curI) * 0.06;
    }

    // ---- Hemisphere intensity rides the same day/night curve. ----
    if (hemiRef.current) {
      const curH = hemiRef.current.intensity;
      hemiRef.current.intensity =
        curH + (scene.hemiIntensity - curH) * 0.06;
    }

    // ---- Tracking mint point light: still follows the active
    //      landmark's world-space position. ----
    if (trackRef.current) {
      tmpPos.set(...scene.landmarkPos);
      trackRef.current.position.copy(tmpPos);
    }

    // ---- Warm rim light: sits opposite the main key light and gives
    //      the low-poly silhouette a quiet premium edge. ----
    if (rimRef.current) {
      rimPos.set(
        -scene.lightPos[0] * 0.72,
        Math.max(1.8, scene.lightPos[1] * 0.45),
        -scene.lightPos[2] * 0.72,
      );
      rimRef.current.position.lerp(rimPos, 0.045);
      const desired = 0.24 + scene.lightIntensity * 0.08;
      rimRef.current.intensity =
        rimRef.current.intensity + (desired - rimRef.current.intensity) * 0.06;
      accentColor.set(scene.atmosphereColor);
      rimRef.current.color.lerp(accentColor, 0.055);
    }

    // A quiet camera-side fill catches the front-facing low-poly facets.
    // It follows the lens rather than the planet, so camera flights reveal
    // fresh geometry instead of flattening the surface into one dark mass.
    if (cameraFillRef.current) {
      fillPos.copy(camera.position).normalize().multiplyScalar(3.8);
      fillPos.x += Math.sin(clock.elapsedTime * 0.24) * 0.35;
      cameraFillRef.current.position.lerp(fillPos, 0.08);
      accentColor.set(scene.inkColor);
      cameraFillRef.current.color.lerp(accentColor, 0.05);
      const desired = 0.16 + scene.hemiIntensity * 0.22;
      cameraFillRef.current.intensity = THREE.MathUtils.lerp(
        cameraFillRef.current.intensity,
        desired,
        0.06,
      );
    }
  });

  // Suppress unused-prop warning while keeping the API flexible.
  void activeSceneIndex;

  return (
    <group name="scene-lights">
      <hemisphereLight
        ref={hemiRef}
        args={[HEMI_TOP, HEMI_BOTTOM, 0.6]}
      />
      <directionalLight
        ref={dirRef}
        color={SCENES[0].lightColor}
        intensity={SCENES[0].lightIntensity}
        position={[...SCENES[0].lightPos]}
      />
      <ambientLight color={AMBIENT_COLOR} intensity={0.16} />
      <pointLight
        ref={trackRef}
        color={TRACK_COLOR}
        intensity={0.44}
        distance={3.4}
        decay={2}
      />
      <pointLight
        ref={rimRef}
        color={RIM_COLOR}
        intensity={0.28}
        distance={8}
        decay={2}
      />
      <pointLight
        ref={cameraFillRef}
        color={SCENES[0].inkColor}
        intensity={0.24}
        distance={7}
        decay={2}
      />
    </group>
  );
}

export default SceneLights;
